import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useLaunchParams } from "@telegram-apps/sdk-react";
import { useInfinitePriceAlertEvents, useCreatePriceAlertConfig, usePriceAlertStats } from "@/hooks/usePriceAlerts";
import { PriceAlertEventTile } from "@/components/PriceAlertEventTile";
import { PriceAlertConfigDrawer } from "@/components/PriceAlertConfigDrawer";
import { PriceAlertAnalyzeDrawer } from "@/components/PriceAlertAnalyzeDrawer";
import { PositionTradeDrawer } from "@/components/PositionTradeDrawer";
import { CreatePriceAlertConfigPayload, PriceAlertEventData } from "@/api/price-alerts";
import { PriceAlertStatsCards } from "@/components/PriceAlertStatsCards";
import { ReferralBottomNav } from "@/components/ReferralBottomNav";
import { Loader2, Plus, Settings } from "lucide-react";
import { ensureUsdtSymbol } from "@/utils/symbols";

const EVENTS_PER_PAGE = 10;

export const PriceAlertsPageMobile: React.FC = () => {
  const navigate = useNavigate();
  const launchParams = useLaunchParams();
  const userId = launchParams.initData?.user?.id?.toString() || "";

  const [showAddDrawer, setShowAddDrawer] = useState(false);
  const [analyzeEvent, setAnalyzeEvent] = useState<PriceAlertEventData | null>(null);
  const [isTradeDrawerOpen, setIsTradeDrawerOpen] = useState(false);
  const [tradeSymbol, setTradeSymbol] = useState<string | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const { data: statsData, isLoading: isLoadingStats } = usePriceAlertStats(userId);

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfinitePriceAlertEvents(userId, EVENTS_PER_PAGE);

  const createMutation = useCreatePriceAlertConfig(userId);

  const events = data?.pages.flatMap((page) => page.events) ?? [];
  const hasMore = hasNextPage ?? false;

  useEffect(() => {
    if (!hasMore || isLoading || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) observer.observe(currentRef);

    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, [hasMore, isLoading, isFetchingNextPage, fetchNextPage]);

  const handleCreate = (payload: CreatePriceAlertConfigPayload) => {
    createMutation.mutate(payload, {
      onSuccess: () => setShowAddDrawer(false),
    });
  };

  const handleOpenTrade = (event: PriceAlertEventData) => {
    setTradeSymbol(ensureUsdtSymbol(event.symbol));
    setIsTradeDrawerOpen(true);
  };

  const handleTradeFromAnalyze = (symbol: string) => {
    setTradeSymbol(symbol);
    setAnalyzeEvent(null);
    setIsTradeDrawerOpen(true);
  };

  return (
    <div className="min-h-screen bg-primary flex flex-col">
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-xl font-bold text-text-primary mb-4">
            Price Alerts
          </h1>

          <div className="mb-4">
            <PriceAlertStatsCards stats={statsData} isLoading={isLoadingStats} />
          </div>

          {isLoading && (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-accent" />
            </div>
          )}

          {!isLoading && events.length === 0 && (
            <div className="text-center py-12">
              <p className="text-text-secondary">No price alerts yet</p>
              <p className="text-text-secondary text-sm mt-1">
                Configure your alerts to start receiving notifications
              </p>
            </div>
          )}

          {!isLoading && (
            <div className="space-y-3">
              {events.map((event) => (
                <PriceAlertEventTile
                  key={event.id}
                  event={event}
                  onAnalyze={setAnalyzeEvent}
                  onTrade={handleOpenTrade}
                />
              ))}
            </div>
          )}

          {!isLoading && hasMore && (
            <div ref={loadMoreRef} className="flex justify-center py-8">
              {isFetchingNextPage && (
                <Loader2 className="w-6 h-6 animate-spin text-accent" />
              )}
            </div>
          )}

          {!isLoading && !hasMore && events.length > 0 && (
            <div className="text-center py-8">
              <p className="text-text-secondary text-sm">
                No more alerts to load
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Floating Action Buttons */}
      <button
        onClick={() => navigate("/price-alerts/manage")}
        className="fixed bottom-36 right-4 z-40 w-14 h-14 rounded-full bg-gray-600 hover:bg-gray-500 flex items-center justify-center shadow-lg transition-all hover:scale-105 active:scale-95"
        aria-label="Manage alerts"
      >
        <Settings className="w-6 h-6 text-white" />
      </button>

      <button
        onClick={() => setShowAddDrawer(true)}
        className="fixed bottom-20 right-4 z-40 w-14 h-14 rounded-full bg-accent hover:bg-accent/80 flex items-center justify-center shadow-lg transition-all hover:scale-105 active:scale-95"
        aria-label="Add price alert"
      >
        <Plus className="w-6 h-6 text-white" />
      </button>

      <ReferralBottomNav />

      {/* Add Alert Drawer */}
      <PriceAlertConfigDrawer
        open={showAddDrawer}
        onOpenChange={setShowAddDrawer}
        onSubmit={(payload) =>
          handleCreate(payload as CreatePriceAlertConfigPayload)
        }
        isLoading={createMutation.isPending}
      />

      {/* Analyze Drawer */}
      <PriceAlertAnalyzeDrawer
        userId={userId}
        event={analyzeEvent}
        open={!!analyzeEvent}
        onOpenChange={(open) => { if (!open) setAnalyzeEvent(null); }}
        onTrade={handleTradeFromAnalyze}
      />

      {/* Trade Drawer */}
      <PositionTradeDrawer
        userId={userId}
        symbol={tradeSymbol}
        open={isTradeDrawerOpen}
        onOpenChange={(open) => {
          setIsTradeDrawerOpen(open);
          if (!open) setTradeSymbol(null);
        }}
        onGoToPosition={() => {}}
      />
    </div>
  );
};
